import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardPathForUser } from "@/lib/authRedirect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, UserPlus } from "lucide-react";
import CoachCodePreview from "@/components/CoachCodePreview";
import { useCoachCodeLookup, normalizeCoachCode } from "@/hooks/useCoachCodeLookup";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { getRememberMe, getRememberedEmail, setRememberedEmail, setRememberMe as saveRememberPreference, unlockLoginPage } from "@/lib/authStorage";
import {
  checkLinkEmail,
  requestLinkVerificationCode,
  verifyLinkCode,
} from "@/lib/linkedAccounts";
import AuthLoadingScreen from "@/components/AuthLoadingScreen";

const AUTH_BOOT_TIMEOUT_MS = 2000;

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, login, register, loginWithGoogle, displayAccounts, savedAccounts } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bootTimedOut, setBootTimedOut] = useState(false);
  const [emailRequiresCode, setEmailRequiresCode] = useState(false);
  const [linkCode, setLinkCode] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [codeVerified, setCodeVerified] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const lastCodeEmailRef = useRef("");
  const [rememberMe, setRememberMe] = useState(() => getRememberMe());
  const [loginForm, setLoginForm] = useState({
    email: getRememberedEmail(),
    password: "",
  });

  const addAccountMode = searchParams.get("add_account") === "1";
  const coachCodeFromUrl = searchParams.get('coach_code') || '';
  const [regForm, setRegForm] = useState({
    email: "", password: "", name: "",
    role: coachCodeFromUrl ? "student" : "coach",
    coach_code: coachCodeFromUrl,
  });
  const [defaultTab, setDefaultTab] = useState(coachCodeFromUrl ? "register" : "login");
  const { preview: coachCodePreview, checking: coachCodeChecking } = useCoachCodeLookup(
    regForm.role === "student" ? regForm.coach_code : ""
  );

  useEffect(() => {
    unlockLoginPage();
  }, []);

  useEffect(() => {
    if (addAccountMode) {
      setLoginForm((prev) => ({ ...prev, email: "", password: "" }));
    }
  }, [addAccountMode]);

  useEffect(() => {
    const timer = setTimeout(() => setBootTimedOut(true), AUTH_BOOT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!authLoading && user && !addAccountMode) {
      window.location.replace(dashboardPathForUser(user));
    }
  }, [user, authLoading, addAccountMode]);

  useEffect(() => {
    if (!addAccountMode || !user || !loginForm.email) {
      setEmailRequiresCode(false);
      setVerificationId(null);
      setCodeVerified(false);
      setLinkCode("");
      lastCodeEmailRef.current = "";
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const { requires_verification } = await checkLinkEmail(loginForm.email);
        setEmailRequiresCode(!!requires_verification);

        if (!requires_verification) {
          setVerificationId(null);
          setCodeVerified(false);
          setLinkCode("");
          lastCodeEmailRef.current = "";
          return;
        }

        if (lastCodeEmailRef.current === loginForm.email) return;

        setCodeSending(true);
        try {
          const sent = await requestLinkVerificationCode(loginForm.email);
          lastCodeEmailRef.current = loginForm.email;
          setVerificationId(sent.verification_id);
          setCodeVerified(false);
          setLinkCode("");
        } catch (err) {
          setEmailRequiresCode(false);
          toast.error(err.response?.data?.detail || "Impossible d'envoyer le code");
        } finally {
          setCodeSending(false);
        }
      } catch {
        setEmailRequiresCode(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [loginForm.email, addAccountMode, user]);

  if (authLoading && !bootTimedOut && !user) {
    return <AuthLoadingScreen />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let activeVerificationId = verificationId;

      if (addAccountMode && emailRequiresCode && !codeVerified) {
        if (!linkCode.trim()) {
          toast.error("Saisissez le code reçu par email");
          setLoading(false);
          return;
        }
        if (!activeVerificationId) {
          const sent = await requestLinkVerificationCode(loginForm.email);
          activeVerificationId = sent.verification_id;
          setVerificationId(sent.verification_id);
        }
        await verifyLinkCode(activeVerificationId, linkCode.trim());
        setCodeVerified(true);
      }

      const loggedInUser = await login(loginForm.email, loginForm.password, rememberMe, {
        addAccount: addAccountMode,
        linkVerificationId: emailRequiresCode ? activeVerificationId : null,
      });
      if (rememberMe) {
        setRememberedEmail(loginForm.email);
      }
      toast.success(addAccountMode ? "Compte ajouté — basculez depuis le menu profil" : "Connexion réussie");
      window.location.replace(dashboardPathForUser(loggedInUser));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const newUser = await register(regForm, { addAccount: addAccountMode });
      toast.success(addAccountMode ? "Compte ajouté avec succès" : "Inscription réussie");
      window.location.replace(dashboardPathForUser(newUser));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/8454951/pexels-photo-8454951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Fitness"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Gérez votre coaching en toute simplicité</h2>
          <p className="mt-3 text-white/70 text-lg">Plans, suivi, paiements. Tout en un seul endroit.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <button
            onClick={() => (addAccountMode && user ? navigate(`/${user.role}/dashboard`) : navigate("/"))}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8 transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4" /> {addAccountMode ? "Retour au tableau de bord" : "Retour"}
          </button>

          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900" data-testid="auth-title">COACH.</h1>
          <p className="mt-2 text-slate-500">
            {addAccountMode
              ? "Ajoutez un compte sans déconnecter le compte actuel"
              : "Connectez-vous ou créez votre compte"}
          </p>

          {addAccountMode && user && (
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/80 p-4 space-y-3" data-testid="add-account-banner">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                  <UserPlus className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900">Compte actuellement actif</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-blue-200 text-blue-800 text-xs">
                        {user.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    Connectez un autre compte : il sera enregistré sur cet appareil et vous pourrez basculer en un clic.
                  </p>
                </div>
              </div>
              {(savedAccounts?.length > 1 || displayAccounts?.length > 1) && (
                <div className="flex flex-wrap gap-1.5 pt-1 border-t border-blue-100">
                  {(displayAccounts || savedAccounts).slice(0, 4).map((acc) => (
                    <Badge
                      key={acc.user_id}
                      variant="outline"
                      className={`text-[10px] ${acc.user_id === user.user_id ? "bg-white border-blue-300" : "bg-blue-50/50"}`}
                    >
                      {acc.name}
                      {acc.user_id === user.user_id ? " · actif" : ""}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <Tabs defaultValue={addAccountMode ? "login" : defaultTab} className="mt-8">
            <TabsList className={`w-full bg-slate-100 ${addAccountMode ? "hidden" : ""}`}>
              <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Connexion</TabsTrigger>
              <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                    className="mt-1.5 h-11 bg-white"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline">Mot de passe oublié ?</Link>
                  </div>
                  <div className="relative mt-1.5">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                      className="h-11 bg-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {addAccountMode && emailRequiresCode && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
                    <p className="text-sm text-slate-600">
                      {codeSending
                        ? "Envoi du code de vérification..."
                        : "Un code de vérification a été envoyé à cette adresse email."}
                    </p>
                    <Input
                      value={linkCode}
                      onChange={(e) => setLinkCode(e.target.value)}
                      placeholder="Code à 6 chiffres"
                      maxLength={6}
                      className="h-11 bg-white"
                      data-testid="link-verification-code-input"
                    />
                  </div>
                )}
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(v) => {
                      const checked = v === true;
                      setRememberMe(checked);
                      saveRememberPreference(checked);
                    }}
                    data-testid="remember-me-checkbox"
                  />
                  <span className="text-sm text-slate-600">Se souvenir de moi</span>
                </label>
                <Button type="submit" disabled={loading || codeSending} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="login-submit-btn">
                  {loading
                    ? addAccountMode
                      ? "Ajout en cours..."
                      : "Connexion..."
                    : addAccountMode
                      ? "Ajouter ce compte"
                      : "Se connecter"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="reg-name">Nom complet</Label>
                  <Input id="reg-name" value={regForm.name} onChange={e => setRegForm({ ...regForm, name: e.target.value })} required data-testid="register-name-input" className="mt-1.5 h-11 bg-white" />
                </div>
                <div>
                  <Label htmlFor="reg-email">Email</Label>
                  <Input id="reg-email" type="email" value={regForm.email} onChange={e => setRegForm({ ...regForm, email: e.target.value })} required data-testid="register-email-input" className="mt-1.5 h-11 bg-white" />
                </div>
                <div>
                  <Label htmlFor="reg-password">Mot de passe</Label>
                  <Input id="reg-password" type="password" value={regForm.password} onChange={e => setRegForm({ ...regForm, password: e.target.value })} required data-testid="register-password-input" className="mt-1.5 h-11 bg-white" />
                </div>
                <div>
                  <Label>Je suis</Label>
                  <Select value={regForm.role} onValueChange={v => setRegForm({ ...regForm, role: v })} data-testid="register-role-select">
                    <SelectTrigger className="mt-1.5 h-11 bg-white" data-testid="register-role-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="coach" data-testid="role-coach-option">Coach</SelectItem>
                      <SelectItem value="student" data-testid="role-student-option">Élève</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {regForm.role === "student" && (
                  <div>
                    <Label htmlFor="reg-code">Code coach (optionnel)</Label>
                    <Input
                      id="reg-code"
                      value={regForm.coach_code}
                      onChange={(e) => setRegForm({ ...regForm, coach_code: normalizeCoachCode(e.target.value) })}
                      placeholder="COACH-XXXXXX"
                      data-testid="register-coach-code-input"
                      className="mt-1.5 h-11 bg-white font-mono"
                    />
                    <CoachCodePreview
                      preview={coachCodePreview}
                      checking={coachCodeChecking}
                      codeLength={regForm.coach_code.trim().length}
                    />
                  </div>
                )}
                <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="register-submit-btn">
                  {loading ? "Inscription..." : "Créer mon compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-sm"><span className="bg-slate-50 px-4 text-slate-400">ou</span></div>
            </div>
            <Button variant="outline" onClick={loginWithGoogle} className="w-full mt-4 h-11 rounded-full border-slate-200 hover:bg-white" data-testid="google-auth-btn">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continuer avec Google
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}